import HttpRequest from "./HttpRequest";
import Helper, {IHttpClientResponseBag} from "../Helpers/Helper";

export interface IGetApprovalRequest {
    TemplateId: string
}

export interface IGetApprovalStepRequest {
    TemplateId: string
}


export interface IGetApprovalActionRequest {
    TemplateId: string
    Mode: string
    Id: string
}

export interface IPostApprovalListMasterRequest {
    Name: string
    Description: string
    AudienceId: string
}

export interface IPutApprovalListMasterRequest {
    Id: string
    Name: string
    Description: string
    AudienceId: string
}

export interface IDeleteApprovalListMasterRequest {
    Id: string
    AudienceId: string
}

export interface IGetApprovalListMasterRequest {
    Mode: string
    Id: string
}

export interface IGetApprovalListDetailRequest {
    Mode: string
    Id: string
}

export interface IPostApprovalListDetailRequest {
    ApprovalListMasterId: string
    Base64File: string
}

export interface IGetApprovalActionResponseRequest {
    TemplateId: string
    Mode: string
    Id: string
}

export interface IGetApprovalStepActionStateRequest {
    TemplateId: string
    Mode: string
    Id: string
}


export interface IGetApprovalStepResponse {
    Id: number
    Name: string
    Order: number
    DateCreated: string
    DateUpdated: string
    ApprovalId: number
}

export interface IGetApprovalStepActionStateResponse {
    ApprovalStepActionId: number
    ApprovalStepActionName: string
    ApprovalStepActionNameResponse: string
    ApprovalStepActionResponseId: number
    ApprovalStepId: number
    ApprovalStepName: string
    DateApprovalSent: string
    DateCreated: string
    DateResponseReceived: string
    DateUpdated: string
    DoAction: string
    DocumentId: number
    DocumentVersionId: number
    EmailAddress: string
    Id: number
    OnEvent: string
    RequestorEmailAddress: string
    TemplateId: number
}

export interface IGetApprovalActionResponse {
    Id: number
    ApprovalStepId: number
    Name: string
    Type: string
    EmailAddressLookupRule: string
    EmailAddress: string
    EmailSubject: string
    EmailTemplate: string
    MapControls: string
    AdditionalNotifys: string
    DateCreated: string
    DateUpdated: string
    UseManagerAttribute: boolean
}

export interface IGetApprovalActionResponseResponse {
    Id: number
    ApprovalStepId: number
    ApprovalStepActionId: number
    OnEvent: string
    DoAction: string
    DoActionType: string
}

export interface IGetApprovalListMasterResponse {
    Id: number
    AudienceId: number
    Name: string
    Description: string
    DateCreated: string
}

export interface IGetApprovalListDetailResponse {
    Id: number
    ApprovalListMasterId: number
    Column1: string
    Column2: string
    Column3: string
    Column4: string
    Column5: string
    Column6: string
}

export interface IPostApprovalRequest {
    TemplateId: string
    Name: string
    Description: string
    Active: boolean
}

export interface IPostApprovalStepRequest {
    TemplateId: string
    ApprovalId: string
    Name: string
}

export interface IPostApprovalActionRequest {
    TemplateId: string
    ApprovalStepId: number
    Name: string
    Type: string
    EmailAddressLookupRule: string
    EmailAddress: string
    EmailSubject: string
    EmailTemplate: string
    MapControls: string
    AdditionalNotifys: string
    UseManagerAttribute: boolean
    Mode: string
}

export interface IPostApprovalAllActionMapControlRequest {
    TemplateId: string
    ApprovalStepId: number
    MapControls: string
    Mode: string
}


export interface IPostApprovalActionResponseActions {
    DoAction: string
    DoActionType: string
}

export interface IPostApprovalActionResponseRequest {
    TemplateId: string
    ApprovalStepId: number
    ApprovalStepActionId: number
    OnEvent: string
    Actions: IPostApprovalActionResponseActions[]
}

export interface IPutApprovalActionRequest {
    Id: number
    TemplateId: string
    ApprovalStepId: number
    Name: string
    Type: string
    EmailAddressLookupRule: string
    EmailAddress: string
    EmailSubject: string
    EmailTemplate: string
    MapControls: string
    AdditionalNotifys: string
    UseManagerAttribute: boolean
}


export interface IDeleteApprovalRequest {
    Id: string
    TemplateId: string
}

export interface IPutApprovalRequest {
    Id: string
    TemplateId: string
    Name: string
    Description: string
    Active: boolean
}

export interface IPutApprovalStepRequest {
    Id: string
    TemplateId: string
    ApprovalId: string
    Name: string
    Order: number
}

export interface IDeleteApprovalStepRequest {
    Id: string
    TemplateId: string
}

export interface IDeleteApprovalActionRequest {
    Id: string
    TemplateId: string
}

export interface IDeleteApprovalActionResponseRequest {
    Id: string
    TemplateId: string
    Operation: string
}

const ApprovalService = {


    /**
     * Asynchronously fetches approval information from the 'approval' endpoint using the provided request object.
     *
     * @function
     * @async
     * @param {IGetApprovalRequest} request - The request object containing the necessary parameters for the HTTP request.
     * @returns {Promise<IHttpClientResponseBag>} - A promise that resolves with the HTTP client response bag if the operation is successful,
     * or rejects with an error object containing the response data if unsuccessful.
     */
    getApproval: async (request: IGetApprovalRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.getWithSession('approval', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },

    /**
     * Sends a post request for approval using session authentication.
     *
     * @param {IPostApprovalRequest} request - The request payload containing the necessary data for the approval.
     * @returns {Promise<IHttpClientResponseBag>} A promise resolving to the HTTP client response bag upon success,
     * or rejecting with the response data if the request fails.
     */
    postApproval: async (request: IPostApprovalRequest): Promise<IHttpClientResponseBag> => {
        let {data}: any = await HttpRequest.postWithSession('approval', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },

    /**
     * Deletes an approval by making an HTTP DELETE request using the provided request details.
     *
     * @param {IDeleteApprovalRequest} request - The request object containing the details needed to identify the approval to delete.
     * @returns {Promise<IHttpClientResponseBag>} A promise that resolves with the response data if the deletion is successful, or rejects with an error object if the deletion fails.
     */
    deleteApproval: async (request: IDeleteApprovalRequest): Promise<IHttpClientResponseBag> => {
        let {data}: any = await HttpRequest.deleteWithSession('approval', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },

    /**
     * Sends an approval put request via HTTP PUT and handles the response asynchronously.
     *
     * @async
     * @function putApproval
     * @param {IPutApprovalRequest} request - The approval request payload to send.
     * @returns {Promise<IHttpClientResponseBag>} Resolves with the response data if successful, rejects with the error response otherwise.
     */
    putApproval: async (request: IPutApprovalRequest): Promise<IHttpClientResponseBag> => {
        let {data}: any = await HttpRequest.putWithSession('approval', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },

    /**
     * Retrieves the approval step details based on the provided request.
     *
     * @async
     * @function
     * @param {IGetApprovalStepRequest} request - The request object containing the necessary parameters for retrieving the approval step.
     * @returns {Promise<IHttpClientResponseBag>} A promise that resolves to an IHttpClientResponseBag object containing the response data or rejects with an error.
     * @throws Will reject the promise if the response indicates a failure.
     */
    getApprovalStep: async (request: IGetApprovalStepRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.getWithSession('approvalstep', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },

    /**
     * Sends a POST request to the 'approvalstep' endpoint with the provided request data
     * and returns a promise with the response.
     *
     * @param {IPostApprovalStepRequest} request - The request payload to be sent to the 'approvalstep' endpoint.
     * @returns {Promise<IHttpClientResponseBag>} A promise resolving with the parsed response object
     * if the request is successful, or rejecting with an error extracted from the response.
     * @throws Throws an error if the 'data.success' flag in the response is false.
     */
    postApprovalStep: async (request: IPostApprovalStepRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.postWithSession('approvalstep', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },

    /**
     * Sends a PUT request to update or create an approval step using the specified request payload.
     *
     * @param {IPutApprovalStepRequest} request - The payload containing the necessary data to update or create an approval step.
     * @returns {Promise<IHttpClientResponseBag>} A promise that resolves with the response data as an IHttpClientResponseBag object,
     *                                            or rejects with an error if the operation fails.
     * @throws Will reject with an error if the response does not indicate success.
     */
    putApprovalStep: async (request: IPutApprovalStepRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.putWithSession('approvalstep', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },

    /**
     * Deletes an approval step by sending a request to the server.
     * Utilizes an authenticated session to perform the operation.
     *
     * @param {IDeleteApprovalStepRequest} request - The request object containing the necessary details for deleting the approval step.
     * @returns {Promise<IHttpClientResponseBag>} A promise that resolves to an IHttpClientResponseBag object if the operation is successful.
     * @throws Will reject the promise with an error object if the operation is unsuccessful.
     */
    deleteApprovalStep: async (request: IDeleteApprovalStepRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.deleteWithSession('approvalstep', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },

    /**
     * Retrieves an approval action based on the specified request.
     *
     * This method sends a GET request to the 'approvalaction' endpoint and expects a response
     * containing the approval action data. If the request is successful, it resolves with the
     * parsed response data. Otherwise, it rejects with an error derived from the response.
     *
     * @param {IGetApprovalActionRequest} request - The request object containing the parameters for the approval action.
     * @returns {Promise<IHttpClientResponseBag>} A promise that resolves with the response bag or rejects with an error.
     */
    getApprovalAction: async (request: IGetApprovalActionRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.getWithSession('approvalaction', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },

    /**
     * Handles the deletion of an approval action by making an HTTP DELETE request.
     *
     * @function
     * @async
     * @param {IDeleteApprovalActionRequest} request - The request object containing the necessary data for deleting the approval action.
     * @returns {Promise<IHttpClientResponseBag>} A promise that resolves with the response bag if the deletion is successful,
     * or rejects with an error if the operation fails.
     */
    deleteApprovalAction: async (request: IDeleteApprovalActionRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.deleteWithSession('approvalaction', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },

    /**
     * Handles the post approval action by sending a POST request to the 'approvalaction' endpoint with the provided request payload.
     * This function processes the response to determine success and resolve or reject with appropriate results.
     *
     * @param {IPostApprovalActionRequest} request - The request payload containing data required for the approval action.
     * @returns {Promise<IHttpClientResponseBag>} A promise that resolves to an IHttpClientResponseBag if the request is successful, or rejects with an error if it is not.
     */
    postApprovalAction: async (request: IPostApprovalActionRequest | IPostApprovalAllActionMapControlRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.postWithSession('approvalaction', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },

    /**
     * Sends a PUT request with session information to perform an approval action.
     *
     * This method handles the API call to the 'approvalaction' endpoint using the provided request object.
     * It returns a promise that resolves to the HTTP client response bag or rejects with an error if the request is unsuccessful.
     *
     * @param {IPutApprovalActionRequest} request - The input object containing the data needed for the approval action.
     * @returns {Promise<IHttpClientResponseBag>} A promise that resolves with the API response or rejects with an error object extracted from the response.
     * @throws Will reject with an error object if the response indicates failure.
     */
    putApprovalAction: async (request: IPutApprovalActionRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.putWithSession('approvalaction', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },

    /**
     * Fetches the approval action response from the server using a provided request object.
     *
     * This function sends a GET request to the `approvalactionresponse` endpoint with the specified request data.
     * The response is parsed, and if successful, a resolved promise containing the parsed data is returned.
     * If the fetch operation fails or the response indicates failure, the promise is rejected with an error.
     *
     * @param {IGetApprovalActionResponseRequest} request - The request object containing necessary information for the API call.
     * @returns {Promise<IHttpClientResponseBag>} A promise resolving to the parsed response data or rejecting with an error.
     * @throws Will reject with an error if the response indicates failure or if the promise encounters an issue.
     */
    getApprovalActionResponse: async (request: IGetApprovalActionResponseRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.getWithSession('approvalactionresponse', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },

    /**
     * Executes a POST request to the 'approvalactionresponse' endpoint with the provided request data.
     * Processes the server's response to determine success or error and parses the response result if successful.
     *
     * @param {IPostApprovalActionResponseRequest} request - The request payload for the approval action response.
     * @returns {Promise<IHttpClientResponseBag>} A promise that resolves with the parsed response data if successful or rejects with an error if the operation fails.
     */
    postApprovalActionResponse: async (request: IPostApprovalActionResponseRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.postWithSession('approvalactionresponse', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },

    /**
     * Deletes an approval action response.
     *
     * This asynchronous function performs an HTTP DELETE request to remove
     * an approval action response based on the provided request data.
     * If the operation is successful, it resolves the promise with the parsed response data.
     * In case of an error, it rejects the promise with the extracted error details.
     *
     * @param {IDeleteApprovalActionResponseRequest} request - The request data required to delete the approval action response.
     * @returns {Promise<IHttpClientResponseBag>} A promise that resolves with the parsed HTTP client response bag or rejects with an error.
     */
    deleteApprovalActionResponse: async (request: IDeleteApprovalActionResponseRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.deleteWithSession('approvalactionresponse', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },

    /**
     * Retrieves the approval list master data based on the provided request parameters.
     *
     * @param {IGetApprovalListMasterRequest} request - The request parameters for fetching the approval list master.
     * @returns {Promise<IHttpClientResponseBag>} A promise that resolves with the approval list master data or rejects with an error.
     * @throws Will reject with an error if the data retrieval is unsuccessful.
     */
    getApprovalListMaster: async (request: IGetApprovalListMasterRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.getWithSession('approvalListMaster', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },


    /**
     * Sends a POST request to the 'approvalactionresponse' endpoint with the provided request payload
     * and processes the response to return a Promise of IHttpClientResponseBag.
     *
     * @param {IPostApprovalListMasterRequest} request - The request payload to be sent to the endpoint.
     * @returns {Promise<IHttpClientResponseBag>} A Promise that resolves to the parsed response as IHttpClientResponseBag
     * or rejects with an error if the request fails.
     *
     * @throws Will throw an error if the response indicates failure (i.e., if `data.success` is false).
     */
    postApprovalListMaster: async (request: IPostApprovalListMasterRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.postWithSession('approvalListMaster', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },

    /**
     * Updates or modifies the approval list master data by sending a PUT request with the provided request payload.
     * The method interacts with the `approvalListMaster` endpoint.
     *
     * @param {IPutApprovalListMasterRequest} request - The payload containing data to update the approval list master.
     * @returns {Promise<IHttpClientResponseBag>} A promise resolving to the response from the server in the form of `IHttpClientResponseBag`.
     * @throws Will reject with an error parsed from the response if the update operation is not successful.
     */
    putApprovalListMaster: async (request: IPutApprovalListMasterRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.putWithSession('approvalListMaster', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },


    /**
     * Deletes an approval list master entry.
     *
     * This method sends an HTTP DELETE request to the 'approvalListMaster' endpoint
     * with the provided request object. It handles the asynchronous response
     * and resolves with the parsed IHttpClientResponseBag if the operation is successful,
     * or rejects with an error if the operation fails.
     *
     * @param {IDeleteApprovalListMasterRequest} request - The request object containing the details required for deletion.
     * @returns {Promise<IHttpClientResponseBag>} A promise that resolves with an IHttpClientResponseBag if the operation succeeds or rejects with an error.
     */
    deleteApprovalListMaster: async (request: IDeleteApprovalListMasterRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.deleteWithSession('approvalListMaster', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },


    /**
     * Fetches the details of the approval list based on the given request data.
     *
     * @param {IGetApprovalListDetailRequest} request - The request object containing the necessary data to fetch the approval list details.
     * @returns {Promise<IHttpClientResponseBag>} A promise that resolves to an IHttpClientResponseBag containing the approval list details.
     * The promise will reject with an error if the server response indicates a failure.
     */
    getApprovalListDetail: async (request: IGetApprovalListDetailRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.getWithSession('approvalListDetail', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },


    /**
     * Sends a POST request to retrieve the details of an approval list.
     *
     * @param {IPostApprovalListDetailRequest} request - The request payload containing the necessary parameters for the approval list detail API.
     * @returns {Promise<IHttpClientResponseBag>} A promise that resolves with the response data, or rejects with an error if the request is unsuccessful.
     * @throws Will throw an error extracted from the server response if the `success` property of the response is false.
     */
    postApprovalListDetail: async (request: IPostApprovalListDetailRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.postWithSession('approvalListDetail', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },


    /**
     * Asynchronously retrieves the approval step action state based on the provided request.
     * This function sends a GET request to the 'approvalStepActionState' endpoint and processes the response.
     *
     * @param {IGetApprovalStepActionStateRequest} request - The request object containing necessary parameters for retrieving the approval step action state.
     * @returns {Promise<IHttpClientResponseBag>} A promise that resolves to the approval step action state response as an IHttpClientResponseBag object.
     * The promise rejects if the response indicates an error.
     */
    getApprovalStepActionState: async (request: IGetApprovalStepActionStateRequest): Promise<IHttpClientResponseBag> => {
        const {data}: any = await HttpRequest.getWithSession('approvalStepActionState', request)

        return new Promise<IHttpClientResponseBag>((resolve, reject) => {
            if (!data.success) reject(Helper.GetErrorFromResponse(data))
            resolve(JSON.parse(data.result) as IHttpClientResponseBag)
        })
    },



}

export default ApprovalService
